/**
 * Pure helpers voor het rustige projectdetailscherm (Joris, 25 sep 2026:
 * "hoe kunnen we dat rustiger maken?"). Tellers en detail-links tonen we alleen
 * als er iets te zien is; nullen staan in de Tabbladen-weergave.
 */

import { T } from '../ProjectOverview/parts/stitch';
import { isTaakAfgerond } from '../ProjectOverview/parts/projectMeta';
import { isDagstaatTaak } from '../../utils/taakStatus';

// Eén tekstmaat voor alles behalve titels; secundaire acties zijn tekstlinks.
export const TEKST = '0.85rem';
export const stilleLinkSx = {
  border: 0, bgcolor: 'transparent', p: 0, m: 0, cursor: 'pointer',
  fontFamily: T.fontBody, fontSize: TEKST, fontWeight: 500, color: T.secondary,
  display: 'inline-flex', alignItems: 'center', gap: 0.5,
  '&:hover': { color: T.text, textDecoration: 'underline' },
  '&:disabled': { opacity: 0.5, cursor: 'default' },
};
/**
 * Stille secundaire zone onderaan de fasekaart: de koppen van gereedschap,
 * documenten en dossier (elk `.zone-deel` met één `.zone-kop`) staan samen op
 * één regel in kleinere grijze tekst; wat je openklapt valt eronder (order 1,
 * volle breedte); `.zone-details` altijd als laatste regel.
 */
export const stilleZoneSx = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 3, rowGap: 1,
  pt: 2, borderTop: `1px solid ${T.surfaceHigh}`,
  '& .zone-deel': { display: 'contents' },
  '& .zone-deel > *': { order: 1, flexBasis: '100%', minWidth: 0 },
  '& .zone-deel > .zone-kop': { order: 0, flexBasis: 'auto', mb: 0 },
  '& .zone-details': { order: 2, flexBasis: '100%' },
  '& .zone-kop, & .zone-kop button, & .zone-kop span, & .zone-kop p, & .zone-details button, & .zone-details p': {
    fontSize: '0.8rem',
  },
  '& button.zone-kop, & .zone-kop button, & .zone-kop p, & .zone-details button, & .zone-details p': {
    color: T.outline, fontWeight: 500,
  },
  '& button.zone-kop:hover, & .zone-kop button:hover, & .zone-details button:hover': { color: T.text },
};

export const stilleKopSx = {
  fontFamily: T.fontBody, fontSize: TEKST, fontWeight: 600, color: T.text,
};

const meervoud = (n, enkel, meer) => `${n} ${n === 1 ? enkel : meer}`;

// Volgorde = volgorde in de stille regel; key = paneel dat de teller opent.
const TELLER_DEFS = [
  { key: 'taken', enkel: 'taak', meer: 'taken' },
  { key: 'deliverables', enkel: 'deliverable', meer: 'deliverables' },
  { key: 'draaidagen', enkel: 'draaidag', meer: 'draaidagen' },
  { key: 'callsheets', enkel: 'callsheet', meer: 'callsheets' },
  { key: 'quitclaims', enkel: 'quitclaim', meer: 'quitclaims' },
  { key: 'notes', enkel: 'notitie', meer: 'notities' },
  { key: 'meetings', enkel: 'meeting', meer: 'meetings' },
  { key: 'documenten', enkel: 'document', meer: 'documenten' },
  { key: 'correspondentie', enkel: 'mail', meer: 'mails' },
];

/**
 * Tellers voor de kop: alleen waarden > 0, als leesbare korte labels.
 * `aantallen` = { taken, takenAf, deliverables, …, correspondentie }; null of
 * undefined (nog aan het laden) telt als 0.
 */
export function stilleTellers(aantallen = {}) {
  return TELLER_DEFS.flatMap(({ key, enkel, meer }) => {
    const n = Number(aantallen[key]) || 0;
    if (n <= 0) return [];
    if (key === 'taken') {
      const af = Math.min(Number(aantallen.takenAf) || 0, n);
      return [{ key, label: `${af}/${n} taken af` }];
    }
    return [{ key, label: meervoud(n, enkel, meer) }];
  });
}

/**
 * Detail-links in de fasekaart splitsen: panelen met een aantal > 0 staan
 * direct in beeld, de rest (0 of onbekend) achter "meer".
 */
export function splitsDetails(panels = [], aantallen = {}) {
  const zichtbaar = [];
  const overig = [];
  panels.forEach((key) => {
    const n = aantallen[key];
    if (typeof n === 'number' && n > 0) zichtbaar.push({ key, aantal: n });
    else overig.push({ key, aantal: typeof n === 'number' ? n : null });
  });
  return { zichtbaar, overig };
}

/**
 * Eén stille statuszin voor de fasekaart: "Nog niet — <eerste bewijs>".
 */
export function faseStatusZin(statusLabel, evidence = []) {
  const eerste = (evidence || [])[0];
  return eerste ? `${statusLabel} — ${eerste}` : statusLabel;
}

/**
 * Vinkjes in de fasebalk eerlijk maken. De engine leidt elke fase los af
 * (Productie = 'klaar' zodra alle draaidagen voorbij zijn), maar een vinkje ná
 * de fase waar het project nog in zit spreekt zichzelf tegen ("Productie ✓"
 * terwijl Preproductie bezig is). Regel: een fase is pas af als hij 'klaar' is
 * én alle aangezette fasen ervóór ook. Een 'klaar' daarna wordt 'todo' met
 * `deels: true` (stille aanduiding in de tooltip). Uitgezette fasen tellen niet
 * mee en blijven ongewijzigd.
 */
export function eerlijkeFasen(phases = []) {
  let allesKlaarTotNu = true;
  return (phases || []).map((p) => {
    if (!p.is_enabled) return p;
    if (p.status !== 'klaar') { allesKlaarTotNu = false; return p; }
    if (allesKlaarTotNu) return p;
    return { ...p, status: 'todo', deels: true };
  });
}

/** Statuslabel voor tooltip/statuszin: 'Deels' voor een naar voren geschoven 'klaar'. */
export function faseLabel(phase, stijlLabel) {
  return phase?.deels ? 'Deels' : stijlLabel;
}

const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const DAG_MS = 24 * 60 * 60 * 1000;

// 'YYYY-MM-DD…' → lokale middernacht (geen tijdzoneschuif bij pure datums).
function alsDag(waarde) {
  if (!waarde) return null;
  const m = String(waarde).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(waarde);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function vandaagDag(vandaag) {
  const v = vandaag instanceof Date ? vandaag : new Date();
  return new Date(v.getFullYear(), v.getMonth(), v.getDate());
}

/** De datum waarop een taak af moet: eind → deadline → start. */
export function taakEinddatum(task) {
  const f = task?.fields || {};
  return alsDag(f.EndDateUitvoerder || f.Deadline || f.StartDateUitvoerder);
}

/** "3 aug" (jaar erbij als het niet dit jaar is). */
export function korteDatum(dag, vandaag) {
  if (!dag) return '';
  const jaar = dag.getFullYear() !== vandaagDag(vandaag).getFullYear() ? ` ${dag.getFullYear()}` : '';
  return `${dag.getDate()} ${MAANDEN[dag.getMonth()]}${jaar}`;
}

/**
 * Datumlabel voor een taakregel: { label, teLaat, dagen }. Te laat = open
 * taak met einddatum vóór vandaag → "3 aug · 53 d te laat".
 */
export function taakDatumInfo(task, vandaag) {
  const dag = taakEinddatum(task);
  if (!dag) return { label: '', teLaat: false, dagen: 0 };
  const v = vandaagDag(vandaag);
  const dagen = Math.round((v - dag) / DAG_MS);
  // Draaidag of vrije dag is nooit te laat: voorbij is voorbij (Joris, 3 nov 2026).
  const teLaat = dagen > 0 && !isTaakAfgerond(task) && !isDagstaatTaak(task);
  const datum = korteDatum(dag, v);
  return { label: teLaat ? `${datum} · ${dagen} d te laat` : datum, teLaat, dagen: teLaat ? dagen : 0 };
}

/** Eerste echte naam van de uitvoerder (geen rec-id's), of ''. */
export function taakEigenaar(task) {
  const f = task?.fields || {};
  const raw = f['Voornaam Assignee'] || f.Assignee || [];
  const lijst = (Array.isArray(raw) ? raw : [raw]).filter((n) => n && !String(n).startsWith('rec'));
  return lijst.length ? String(lijst[0]) : '';
}

// Kalenderblokken zijn geen 'volgende stap' (een draaidag wél).
const GEEN_WERK_TYPES = new Set(['Afspraak', 'Vrij/intern', 'Vrije dag', 'Vanallesuurtje']);

const lijstVan = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/** Naam van de deliverable waar een (deadline-)taak aan hangt; anders de taaknaam zonder "Deadline:". */
function deliverableNaamVoor(taak, deliverables = []) {
  const id = lijstVan(taak.fields?.Deliverable)[0];
  const d = id ? (deliverables || []).find((x) => x.id === id) : null;
  const naam = d?.fields?.['Naam van Deliverable'] || d?.fields?.Deliverable || d?.fields?.Naam;
  if (naam) return String(naam);
  return String(taak.fields?.NaamTaak || '').replace(/^\s*deadline\s*:?\s*/i, '').trim() || 'deliverable';
}

/**
 * Eén statusregel voor de fasekaart: de eerstvolgende open taak van het
 * project (vroegste datum eerst — te late taken dus voorop; zonder datum
 * achteraan). "Volgende: <taak>, <datum> · <eigenaar of 'geen eigenaar'>".
 * Geeft null als er niets open staat.
 *
 * Een Type=Deadline-taak is geen werk maar een markering op een deliverable;
 * "· geen eigenaar" klopte daar nooit (9604, 23 okt 2026). Die regel wordt
 * "Deadline <deliverable> verlopen sinds 25 aug · 3 taken open", met het aantal
 * open taken van die deliverable. De markering wordt niet automatisch gesloten.
 */
export function volgendeStap(tasks = [], vandaag, deliverables = []) {
  const open = (tasks || []).filter((t) => !isTaakAfgerond(t)
    && !GEEN_WERK_TYPES.has(t.fields?.Type) && !GEEN_WERK_TYPES.has(t.fields?.PrioNr));
  if (!open.length) return null;
  const sleutel = (t) => { const d = taakEinddatum(t); return d ? d.getTime() : Infinity; };
  const [eerste] = [...open].sort((a, b) => sleutel(a) - sleutel(b));
  const info = taakDatumInfo(eerste, vandaag);
  if (eerste.fields?.Type === 'Deadline') {
    const delId = lijstVan(eerste.fields?.Deliverable)[0];
    const nOpen = delId
      ? open.filter((t) => t !== eerste && t.fields?.Type !== 'Deadline' && lijstVan(t.fields?.Deliverable).includes(delId)).length
      : 0;
    const naam = `Deadline ${deliverableNaamVoor(eerste, deliverables)}`;
    const dag = korteDatum(taakEinddatum(eerste), vandaag);
    const datum = info.teLaat ? `verlopen sinds ${dag}` : (dag || 'geen datum');
    const eigenaar = nOpen ? `${nOpen} ${nOpen === 1 ? 'taak' : 'taken'} open` : 'geen taken open';
    return {
      taak: eerste, naam, datum, teLaat: info.teLaat, eigenaar, deadline: true, takenOpen: nOpen,
      tekst: `Volgende: ${naam} ${datum} · ${eigenaar}`,
    };
  }
  const naam = eerste.fields?.NaamTaak || 'Naamloze taak';
  const eigenaar = taakEigenaar(eerste) || 'geen eigenaar';
  return {
    taak: eerste,
    naam,
    datum: info.label || 'geen datum',
    teLaat: info.teLaat,
    eigenaar,
    tekst: `Volgende: ${naam}, ${info.label || 'geen datum'} · ${eigenaar}`,
  };
}
